import { Component, OnInit, OnDestroy, AfterViewInit, Inject } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { Observable, Subject, Subscription, filter, pipe } from "rxjs";
import {
    map,
    distinctUntilChanged,
    takeUntil,
    switchMap,
    combineLatest,
    combineLatestWith,
} from "rxjs/operators";
import { CommonStateService, MenuItem } from "../state";
import { environment } from "../environments/environment";
// TODO: to review.
import { ErrorHandlerService } from "./errorhandler/error-handler.service";
import { ErrorObject } from "./errorhandler/error-object";
import { ErrorTypeEnum } from "./shared/enums.model";
import { CustomToastOption } from "./shared/custom-toast-options";
import { MasterDataStateService } from "../state/master-data/service";
import { defineCustomElements, addTheme } from "corporate-ui";
import { theme as scaniaTheme } from "scania-theme";
import {
    MSAL_GUARD_CONFIG,
    MsalBroadcastService,
    MsalGuardConfiguration,
    MsalService,
} from "@azure/msal-angular";
import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionStatus,
    PopupRequest,
    RedirectRequest,
} from "@azure/msal-browser";
import { HttpClient } from "@angular/common/http";
import { get } from "lodash";
import { AuthService } from "./shared/auth.service";
import { RecalculationHubService } from "../api/services/recalculation-hub.service";


defineCustomElements([
    "c-theme",
    "c-header",
    "c-footer",
    "c-content",
    "c-navigation",
    "c-icon",
]);
addTheme(scaniaTheme);

@Component({
    selector: "#app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.css"],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    profile$: Observable<any>;
    menu$: Observable<MenuItem[]>;
    activeMenu$: Observable<MenuItem>;
    subscriptions = new Subscription();
    isLoggedIn$: Observable<boolean>;

    //new added
    private readonly _destroying$ = new Subject<void>();
    account: AccountInfo | null = null;

    constructor(
        private authService: AuthService,
        private commonService: CommonStateService,
        private errHandler: ErrorHandlerService,
        private toastr: ToastrService,
        private masterDataService: MasterDataStateService,
        private recalculationHubService: RecalculationHubService,
    ) {
        this.authService.userInfo.subscribe((account) => {
            this.account = account;
            console.log("Current account:", this.account);
        });
        this.profile$ = this.authService.userInfo.pipe(
            distinctUntilChanged(),
            map((account) => (account ? account : {}))
        );

        this.subscriptions.add(
            this.authService.userInfo.subscribe((user) => {
                if (
                    !!user &&
                    user.username != null &&
                    this.authService.validateToken(user.idTokenClaims)
                ) {
                    this.masterDataService.loadMasterData();
                    this.recalculationHubService.startConnection();
                }
            })
        );

        this.menu$ = this.commonService.getMenus();
        this.activeMenu$ = this.commonService.getActiveMenu();
        this.subscriptions.add(
            this.errHandler.errorEvent.subscribe((err: ErrorObject) => this.onError(err))
        );

    }

    ngOnInit(): void {

        this.isLoggedIn$ = this.authService.isUserLoggedIn.pipe(
            combineLatestWith(this.authService.isUserAuthorized$),
            map(([isUserLoggedIn, isUserAuthorized]) => isUserLoggedIn && isUserAuthorized)
        );

        document.body.setAttribute("data-env", environment.currentEnvironment);
        if (environment.currentEnvironment !== "prod") {
            document.title += " " + environment.currentEnvironment;
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    ngAfterViewInit(): void {
        this.animateHeaderLogo();
    }

    // c-header renders the logo as a shadow-DOM pseudo-element with no exposed
    // CSS hook, so the spin animation has to be injected into its shadow root.
    // c-header re-renders its shadow DOM on internal state changes (nav toggle,
    // theme updates) which drops our injected <style>, so keep re-applying it.
    private animateHeaderLogo(): void {
        customElements.whenDefined("c-header").then(() => {
            const header = document.querySelector("c-header") as (HTMLElement & { shadowRoot: ShadowRoot }) | null;
            if (!header) {
                return;
            }

            const ensureStyleTag = (shadowRoot: ShadowRoot): boolean => {
                const symbol = shadowRoot.querySelector(".navbar-symbol");
                if (!symbol) {
                    return false;
                }
                if (!shadowRoot.getElementById("calsium-logo-spin-style")) {
                    const style = document.createElement("style");
                    style.id = "calsium-logo-spin-style";
                    style.textContent = `
                        @keyframes calsium-logo-spin {
                            from { transform: rotateY(0deg); }
                            to { transform: rotateY(360deg); }
                        }
                        .navbar-symbol {
                            perspective: 200px;
                        }
                        .navbar-symbol:before {
                            animation: calsium-logo-spin 12s linear infinite;
                            backface-visibility: visible;
                            will-change: transform;
                        }
                    `;
                    shadowRoot.appendChild(style);
                }
                return true;
            };

            const waitForShadowRoot = () => {
                const shadowRoot = header.shadowRoot;
                if (!shadowRoot) {
                    requestAnimationFrame(waitForShadowRoot);
                    return;
                }
                ensureStyleTag(shadowRoot);
                new MutationObserver(() => ensureStyleTag(shadowRoot))
                    .observe(shadowRoot, { childList: true, subtree: true });
            };

            waitForShadowRoot();
        });
    }

    // TODO: add to common state.
    onError(error: ErrorObject) {
        const errorObj = this.errHandler.getError();
        if (errorObj != null) {
            if (errorObj.errType === ErrorTypeEnum.Error) {
                const errOption = new CustomToastOption().getErrorOptions();
                this.toastr.error(
                    errorObj.errorText,
                    errorObj.originator,
                    errOption
                );
            } else if (errorObj.errType === ErrorTypeEnum.Info) {
                const infoOption = new CustomToastOption().getInfoOptions();
                this.toastr.info(
                    errorObj.errorText,
                    errorObj.originator,
                    infoOption
                );
            } else if (errorObj.errType === ErrorTypeEnum.Success) {
                const successOption =
                    new CustomToastOption().getSuccessOptions();
                this.toastr.success(
                    errorObj.errorText,
                    errorObj.originator,
                    successOption
                );
            } else if (errorObj.errType === ErrorTypeEnum.Warning) {
                const warnOption = new CustomToastOption().getWarningOptions();
                this.toastr.warning(
                    errorObj.errorText,
                    errorObj.originator,
                    warnOption
                );
            } else if (errorObj.errType === ErrorTypeEnum.Custom) {
                const custOption = new CustomToastOption().getCustomOptions(
                    "customErrMsgClass",
                    "customTitleClass"
                );
                this.toastr.error(
                    errorObj.errorText,
                    errorObj.originator,
                    custOption
                );
            }
        }
    }
}
